import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import { useAuth } from "../../context/auth";
import Sidebar from "../../components/Dashboard/Sidebar";
import axios from "axios";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { AiOutlineReload } from "react-icons/ai";
import { FiGrid, FiPlus } from "react-icons/fi";
import { getImageUrl } from "../../utils/imageHelper";

const Products = () => {
  const [auth, setAuth] = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const getTotal = async () => {
    try {
      const { data } = await axios.get("/api/v1/product/product-count");
      setTotal(data?.total);
    } catch (error) {
      console.log(error);
    }
  };

  const getAllProducts = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`/api/v1/product/product-list/${page}`);
      setProducts(data.products);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error("Something went wrong!");
    }
  };

  const loadMore = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`/api/v1/product/product-list/${page}`);
      setLoading(false);
      setProducts((prev) => [...prev, ...data?.products]);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    getTotal();
    getAllProducts();
  }, []);

  useEffect(() => {
    if (page === 1) return;
    loadMore();
  }, [page]);

  const handleLogout = () => {
    setAuth({ user: null, token: "" });
    localStorage.removeItem("auth");
    navigate("/login");
    setTimeout(() => window.location.reload(), 100);
  };

  if (loading && page === 1) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-4 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600 text-base sm:text-lg">
            Loading Products...
          </p>
        </div>
      </div>
    );
  }

  return (
    <Layout title="Products Management">
      <div className="min-h-screen bg-gray-50 mt-16 sm:mt-20">
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 flex flex-wrap items-center gap-2 sm:gap-4">
                  <span className="truncate">Product Management</span>
                  <span className="inline-flex items-center px-2.5 sm:px-3 py-1 sm:py-1.5 bg-gray-900 text-white text-xs sm:text-base font-semibold rounded-full shadow flex-shrink-0">
                    <FiGrid className="mr-1 sm:mr-1.5" size={16} />
                    {total}
                    <span className="ml-1 text-xs font-medium text-gray-300 hidden sm:inline">
                      Products
                    </span>
                  </span>
                </h1>
                <p className="text-gray-600 mt-1.5 sm:mt-2 text-xs sm:text-sm md:text-base">
                  Click on the Product to Edit Product Info.
                </p>
              </div>

              <button
                onClick={() => navigate("/dashboard/admin/create-product")}
                className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-900 text-white text-sm sm:text-base font-semibold rounded-lg hover:bg-black transition flex-shrink-0 w-full sm:w-auto"
              >
                <FiPlus size={18} />
                <span>Add Product</span>
              </button>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
            <aside className="w-full lg:w-64 xl:w-72 flex-shrink-0">
              <Sidebar
                auth={auth}
                activeTab="products"
                setActiveTab={() => {}}
                onLogout={handleLogout}
              />
            </aside>

            <main className="flex-1 min-w-0">
              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5 lg:gap-6">
                {products?.map((p) => (
                  <Link
                    key={p._id}
                    to={`/dashboard/admin/product/${p.slug}`}
                    className="bg-white rounded-lg shadow p-3 sm:p-4 flex flex-col cursor-pointer hover:shadow-lg transition-shadow group"
                    title={p.name}
                  >
                    <div className="relative overflow-hidden rounded-md mb-3 sm:mb-4 bg-gray-100">
                      <img
                        src={getImageUrl(p.photos[0])}
                        onError={(e) => {
                          e.target.src = "/images/fallback-RB.png";
                        }}
                        alt={p.name}
                        className="h-36 sm:h-40 md:h-44 lg:h-48 w-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <h5 className="font-semibold text-sm sm:text-base lg:text-lg text-gray-900 mb-1 sm:mb-1.5 truncate">
                      {p.name}
                    </h5>
                    <p className="text-gray-600 text-xs sm:text-sm line-clamp-2 flex-1">
                      {p.description}
                    </p>
                  </Link>
                ))}
              </div>

              {products.length === 0 && !loading && (
                <div className="text-center py-12 sm:py-16">
                  <FiGrid className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mb-4" />
                  <p className="text-gray-500 text-base sm:text-lg">
                    No products found
                  </p>
                  <button
                    onClick={() => navigate("/dashboard/admin/create-product")}
                    className="mt-4 inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-gray-900 text-white text-sm sm:text-base font-semibold rounded-lg hover:bg-black transition"
                  >
                    <FiPlus size={18} /> Add Your First Product
                  </button>
                </div>
              )}

              {products.length < total && (
                <div className="text-center mt-6 sm:mt-8">
                  <button
                    disabled={loading}
                    onClick={(e) => {
                      e.preventDefault();
                      setPage(page + 1);
                    }}
                    className="bg-yellow-400 hover:bg-yellow-500 disabled:bg-yellow-300 disabled:cursor-not-allowed text-gray-900 font-semibold py-2.5 sm:py-3 px-5 sm:px-8 rounded-lg inline-flex items-center gap-2 transition text-sm sm:text-base w-full sm:w-auto justify-center"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                        <span>Loading...</span>
                      </>
                    ) : (
                      <>
                        <span>Load More</span>
                        <AiOutlineReload className="group-hover:rotate-180 transition-transform duration-300" />
                      </>
                    )}
                  </button>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Products;
